import { useAuthContext } from "../contexts/AuthContext";
import {
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  sendVerificationEmail as apiSendVerificationEmail,
  verifyEmail as apiVerifyEmail,
} from "../api/auth";
import { RegistrationPayload, AuthenticationResponse } from "../types/api";
import { AUTH_TOKEN_KEY } from "../constants";

/**
 * Custom hook for authentication logic.
 *
 * Provides login and logout functions, as well as authentication state.
 * Integrates with AuthContext for centralized authentication management.
 *
 * @returns {Object} - Authentication state and actions:
 * - isAuthenticated: boolean indicating if user is authenticated
 * - isLoading: boolean indicating if authentication is in progress
 * - user: DecodedToken or null
 * - login: function to authenticate user with email and password
 * - logout: function to log out the user
 */
export const useAuth = () => {
  // Destructure authentication state and actions from context
  const {
    isAuthenticated,
    isLoading,
    loginUser,
    logoutUser,
    setIsEmailVerified,
  } = useAuthContext();

  /**
   * Authenticates the user with the provided email and password.
   * Calls the API, retrieves the JWT token, and updates the authentication context.
   *
   * @param {string} email - The user's email address.
   * @param {string} password - The user's password.
   * @returns {Promise<void>} - The authentication response.
   * @throws {Error} - Throws if authentication fails.
   */
  const login = async (email: string, password: string): Promise<void> => {
    let response: AuthenticationResponse;
    try {
      // Call the API to authenticate and get the user
      response = await apiLogin(email, password);
    } catch (error) {
      // Log and rethrow error for upstream handling
      console.error("Login failed:", error);
      throw error;
    }

    // Update the user in the context
    await loginUser(response.user);

    // This is only useful during PR deployments, when the different
    // PR instance URLs don't have associated `.materiom.org` URLs, which
    // results in our HttpOnly JWT Token cookie being dropped.
    // FIXME: Once we move to a third party authentication service, we can remove this.
    if (process.env.REACT_APP_STAGING_ENVIRONMENT === "dev") {
      localStorage.setItem(AUTH_TOKEN_KEY, response.token);
    }
  };

  /**
   * Registers a new user with the provided registration payload.
   * Calls the registration API.
   *
   * @param {RegistrationPayload} payload - The registration data.
   * @returns {Promise<void>} - The authentication response.
   */
  const registerUser = async (payload: RegistrationPayload): Promise<void> => {
    let response: AuthenticationResponse;
    try {
      response = await apiRegister(payload);
    } catch (error) {
      console.error("Registration failed:", error);
      throw error;
    }

    // Update the user in the context
    await loginUser(response.user);

    // This is only useful during PR deployments, when the different
    // PR instance URLs don't have associated `.materiom.org` URLs, which
    // results in our HttpOnly JWT Token cookie being dropped.
    // FIXME: Once we move to a third party authentication service, we can remove this.
    if (process.env.REACT_APP_STAGING_ENVIRONMENT === "dev") {
      localStorage.setItem(AUTH_TOKEN_KEY, response.token);
    }
  };

  /**
   * Sends a verification email to the user.
   *
   * @returns {Promise<{ status: string }>} - The response from the API.
   */
  const sendVerificationEmailLink = async (): Promise<{ status: string }> => {
    try {
      const response = await apiSendVerificationEmail();
      return response;
    } catch (error) {
      console.error("Sending verification email failed:", error);
      throw error;
    }
  };

  /**
   * Verifies the user's email with the provided token and ID.
   *
   * @param id - The user's ID.
   * @param hash - The hash from the verification link.
   * @param expires - The expiration timestamp from the verification link.
   * @returns {Promise<{ status: string }>} - The response from the API.
   */
  const verifyUserEmail = async (
    id: string,
    hash: string,
    expires: string
  ): Promise<{ status: string }> => {
    try {
      const response = await apiVerifyEmail(id, hash, expires);
      setIsEmailVerified(true);
      return response;
    } catch (error) {
      console.error("Email verification failed:", error);
      throw error;
    }
  };

  /**
   * Logs out the user by clearing authentication tokens and state.
   * Calls the logout API to invalidate the JWT token server-side,
   * then updates the local authentication context.
   *
   * @returns {Promise<void>} - Resolves when logout is complete.
   */
  const logout = async () => {
    // Call the logout API
    await apiLogout();

    // Logout the user from the state
    logoutUser();

    // Remove the JWT token from localStorage
    // FIXME: Once we move to a third party authentication service, we can remove this.
    if (process.env.REACT_APP_STAGING_ENVIRONMENT === "dev") {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  };

  // Return authentication state and actions
  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    registerUser,
    sendVerificationEmailLink,
    verifyUserEmail,
  };
};
